<script setup>
import Footer from './FooterSection.vue'
import Breadcrumbs from './BreadCrumbs.vue'
import BcsSearchDropdown from './BcsSearchDropdown.vue'
import ContentLibraryItemCard from './ContentLibraryItemCard.vue'
import FilterDropdown from './common/FilterDropdown.vue'
import AlertNotification from './AlertNotification.vue'
import WhiteLabelModal from './WhiteLabelModal.vue'
import BcsTooltip from './BcsTooltip.vue'
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useContentLibraryItemFilters } from '../composables/useContentLibraryItemFilters'
import { useContentLibraryDownloadAlert } from '../composables/useContentLibraryDownloadAlert'
import { useAgencyBranding } from '../composables/useAgencyBranding'
import { useAuthStore } from '../stores/AuthStore'
import { PERMISSION_KEYS } from '../access/permissionKeys'
import { useSessionStorageStore } from '../stores/SessionStorage'
import {
  getDatabase,
  ref as dbRef,
  get as dbGet,
} from 'firebase/database'
import { getApp } from 'firebase/app'
import { handleErrors } from '../utils/errorHandler'
import { isEmbeddableContentLibraryUrl } from '../utils/contentLibraryHelpers'
import { ContentLibraryService } from '../services/ContentLibraryService'
import { ContentLibraryTemplateService } from '../services/ContentLibraryTemplateService'
import { useContentLibraryDocumentDownloadActions, isBrandableContentLibraryItem } from '../composables/useContentLibraryDocumentDownloadActions'
import learnUnderstandIcon from '@/assets/images/learn-understand.svg'
import prepareCommunicateIcon from '@/assets/images/prepare-communicate.svg'
import planTrackComplyIcon from '@/assets/images/plan-track-comply.svg'
import toolsCalculatorsIcon from '@/assets/images/tools-calculators.svg'
import newslettersUpdatesIcon from '@/assets/images/newsletters-updates.svg'
import padlockAnnouncementIcon from '@/assets/images/padlock_announcement.svg'
import whiteLabelingAvailableIcon from '@/assets/images/white-labeling-available.svg'

const CONTENT_LIBRARY_UPGRADE_SETTINGS_PATH = 'BCS/Home/Help and Resources/contentLibraryUpgrade'

const db = getDatabase(getApp())
const contentLibraryIntro = ref()
const customNavigation = inject('customNavigation')
const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})
const sessionStorageStore = useSessionStorageStore()
const contentLibraryService = ContentLibraryService()
const contentLibraryTemplateService = ContentLibraryTemplateService()
const authStore = useAuthStore()
const { logoPath, logoSize, loadBranding } = useAgencyBranding()
const shouldShowLockedState = computed(() => (
  !authStore.hasPermission(PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS)
))
const hasCoreAccess = computed(() => authStore.hasPermission(PERMISSION_KEYS.CORE_ACCESS))
const { downloadAlert, dismissDownloadAlert, reportDownloadFailure } = useContentLibraryDownloadAlert()
const {
  getDocumentActions,
  getDocumentActionLabel,
  getDocumentActionIcon
} = useContentLibraryDocumentDownloadActions()
const taxonomyGroupIcons = {
  learnUnderstand: learnUnderstandIcon,
  prepareCommunicate: prepareCommunicateIcon,
  planTrackComply: planTrackComplyIcon,
  toolsCalculators: toolsCalculatorsIcon,
  newslettersUpdates: newslettersUpdatesIcon
}

const taxonomyGroupCards = ref([])
const searchableItems = ref([])
const searchResultCategories = ref([])
const favoriteItemIds = ref(new Set())
const downloadStatsByItemId = ref(new Map())
const favoriteActionLoadingIds = ref(new Set())
const isInitialLoading = ref(true)
const isSearchActive = ref(false)
const showTopButton = ref(false)
const copyPasswordSnackbarMessage = ref('')
const whiteLabelModalRef = ref(null)
const pendingBrandingDownload = ref(null)
const upgradeMailtoRecipient = ref('')
const upgradeMailtoSubject = ref('')
const upgradeMailtoBody = ref('')
let copyPasswordSnackbarTimeoutId = null


const upgradeMailtoHref = computed(() => {
  if (!upgradeMailtoRecipient.value || !upgradeMailtoBody.value) {
    return ''
  }

  const queryParts = []

  if (upgradeMailtoSubject.value) {
    queryParts.push(`subject=${encodeURIComponent(upgradeMailtoSubject.value)}`)
  }

  queryParts.push(`body=${encodeURIComponent(upgradeMailtoBody.value)}`)

  return `mailto:${upgradeMailtoRecipient.value}?${queryParts.join('&')}`
})

const getSortableItemTitle = (item) => {
  return item.libraryFileName || item.documentDisplayName || 'Untitled item'
}

const {
  sortByOptions,
  selectedFilters,
  selectedSortBy,
  getFilterDefinitionsWithCounts,
  applyItemViewStateToCategories,
  sortItemsAlphabetically
} = useContentLibraryItemFilters({
  favoriteItemIds,
  downloadStatsByItemId,
  getSortableItemTitle
})

const countedFilterDefinitions = computed(() => {
  return getFilterDefinitionsWithCounts(searchResultCategories.value)
})

const filteredSearchResultCategories = computed(() => {
  return applyItemViewStateToCategories(searchResultCategories.value)
})

const filteredSearchResultsCount = computed(() => {
  return filteredSearchResultCategories.value.reduce((total, category) => total + (category.items?.length || 0), 0)
})

onMounted(async () => {
  try {
    const initialLoaders = [
      getContentLibraryIntro(),
      loadUpgradeMailtoSettings()
    ]

    if (shouldShowLockedState.value) {
      initialLoaders.push(loadTaxonomyGroupCards())
    } else {
      initialLoaders.push(
        loadSearchableLibraryData(),
        loadFavoriteItemIds(),
        loadDownloadStatsByItemId(),
        loadBranding()
      )
    }

    await Promise.all(initialLoaders)
  } finally {
    isInitialLoading.value = false
  }

  window.scrollTo({ top: 0, behavior: 'smooth' })
  window.addEventListener('scroll', handleWindowScroll)
  handleWindowScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleWindowScroll)

  if (copyPasswordSnackbarTimeoutId) {
    window.clearTimeout(copyPasswordSnackbarTimeoutId)
    copyPasswordSnackbarTimeoutId = null
  }
})

const getContentLibraryIntro = async () => {
  try {
    const contentLibraryIntroRef = dbRef(db, 'BCS/Home/Help and Resources/contentLibraryIntro/default_value')
    const snapshot = await dbGet(contentLibraryIntroRef)
    if (snapshot.exists()) {
      contentLibraryIntro.value = snapshot.val()
    }
  } catch (err) {
    handleErrors('Error fetching content library intro', err)
  }
}

const loadUpgradeMailtoSettings = async () => {
  try {
    const settingsRef = dbRef(db, CONTENT_LIBRARY_UPGRADE_SETTINGS_PATH)
    const snapshot = await dbGet(settingsRef)
    const settings = snapshot.exists() ? snapshot.val() || {} : {}

    upgradeMailtoRecipient.value = settings.recipient || ''
    upgradeMailtoSubject.value = settings.subject || ''
    upgradeMailtoBody.value = settings.body || ''

    if (!upgradeMailtoRecipient.value || !upgradeMailtoBody.value) {
      handleErrors('BcsContentLibrary.loadUpgradeMailtoSettings.missingSettings', new Error('Content Library upgrade mailto settings are missing from Firebase Realtime Database'))
    }
  } catch (error) {
    handleErrors('BcsContentLibrary.loadUpgradeMailtoSettings', error)
  }
}

const handleWindowScroll = () => {
  showTopButton.value = (window.scrollY || 0) > 120
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const goToHelpAndResources = () => {
  const navigation = customNavigation?.value
  if (!navigation) {
    return
  }

  navigation.navigate({
    newPage: navigation.routes.helpAndResources.name
  })
}

const mapTaxonomyGroupCards = (taxonomyGroups = []) => {
  const activeTaxonomyGroups = taxonomyGroups.filter((taxonomyGroup) => taxonomyGroup.isActive !== false)

  return activeTaxonomyGroups.map((taxonomyGroup) => ({
    id: taxonomyGroup.id,
    title: taxonomyGroup.name,
    description: taxonomyGroup.description,
    taxonomyGroupId: taxonomyGroup.id,
    buttonLabel: taxonomyGroup.buttonLabel,
    iconKey: taxonomyGroup.iconKey
  }))
}

const loadTaxonomyGroupCards = async () => {
  try {
    const taxonomyGroups = await contentLibraryService.getTaxonomyGroups({ activeOnly: true })

    taxonomyGroupCards.value = mapTaxonomyGroupCards(taxonomyGroups)
  } catch (err) {
    handleErrors('BcsContentLibrary.loadTaxonomyGroupCards', err)
    taxonomyGroupCards.value = []
  }
}

const loadSearchableLibraryData = async () => {
  try {
    const [taxonomyGroups, categories, items] = await Promise.all([
      contentLibraryService.getTaxonomyGroups({ activeOnly: true }),
      contentLibraryService.getCategories(),
      contentLibraryService.getItems()
    ])

    const activeTaxonomyGroups = taxonomyGroups.filter((taxonomyGroup) => taxonomyGroup.isActive !== false)
    taxonomyGroupCards.value = mapTaxonomyGroupCards(activeTaxonomyGroups)
    const taxonomyGroupsById = new Map(activeTaxonomyGroups.map((taxonomyGroup) => [taxonomyGroup.id, taxonomyGroup]))
    const categoriesById = new Map(
      categories
        .filter((category) => taxonomyGroupsById.has(category.taxonomyGroupId))
        .map((category) => [category.id, category])
    )

    searchableItems.value = items
      .map((item) => {
        const category = categoriesById.get(item.libraryCategoryId)
        const taxonomyGroup = category ? taxonomyGroupsById.get(category.taxonomyGroupId) : null

        if (!category || !taxonomyGroup) {
          return null
        }

        return {
          ...item,
          title: item.libraryFileName || item.documentDisplayName || 'Untitled item',
          description: item.description || category.name || taxonomyGroup.name || '',
          taxonomyGroupId: taxonomyGroup.id,
          taxonomyGroupTitle: taxonomyGroup.name,
          taxonomyGroupDescription: taxonomyGroup.description || '',
          categoryId: category.id,
          categoryName: category.name,
          categoryDescription: category.description || ''
        }
      })
      .filter(Boolean)
  } catch (err) {
    handleErrors('BcsContentLibrary.loadSearchableLibraryData', err)
    taxonomyGroupCards.value = []
    searchableItems.value = []
    searchResultCategories.value = []
  }
}

const loadFavoriteItemIds = async () => {
  try {
    const itemIds = await contentLibraryService.getFavoriteItemIds()
    favoriteItemIds.value = new Set(itemIds)
  } catch (error) {
    handleErrors('BcsContentLibrary.loadFavoriteItemIds', error)
    favoriteItemIds.value = new Set()
  }
}

const loadDownloadStatsByItemId = async () => {
  try {
    downloadStatsByItemId.value = await contentLibraryService.getDownloadStatsByItemId()
  } catch (error) {
    handleErrors('BcsContentLibrary.loadDownloadStatsByItemId', error)
    downloadStatsByItemId.value = new Map()
  }
}

const localeCompareOptions = { sensitivity: 'base' }

const buildGroupedSearchResults = (results = []) => {
  const groupedTaxonomyMap = new Map()

  results.forEach((item) => {
    if (!groupedTaxonomyMap.has(item.taxonomyGroupId)) {
      groupedTaxonomyMap.set(item.taxonomyGroupId, {
        id: item.taxonomyGroupId,
        title: item.taxonomyGroupTitle,
        description: item.taxonomyGroupDescription,
        categoriesMap: new Map()
      })
    }

    const taxonomyGroup = groupedTaxonomyMap.get(item.taxonomyGroupId)

    if (!taxonomyGroup.categoriesMap.has(item.categoryId)) {
      taxonomyGroup.categoriesMap.set(item.categoryId, {
        id: item.categoryId,
        taxonomyGroupId: item.taxonomyGroupId,
        taxonomyGroupTitle: item.taxonomyGroupTitle,
        taxonomyGroupDescription: item.taxonomyGroupDescription,
        name: item.categoryName,
        description: item.categoryDescription,
        items: []
      })
    }

    taxonomyGroup.categoriesMap.get(item.categoryId).items.push(item)
  })

  const sortedTaxonomyGroups = Array.from(groupedTaxonomyMap.values()).sort((firstGroup, secondGroup) => {
    return firstGroup.title.localeCompare(secondGroup.title, undefined, localeCompareOptions)
  })

  return sortedTaxonomyGroups.flatMap((taxonomyGroup) => {
    const sortedCategories = Array.from(taxonomyGroup.categoriesMap.values()).sort((firstCategory, secondCategory) => {
      return firstCategory.name.localeCompare(secondCategory.name, undefined, localeCompareOptions)
    })

    return sortedCategories.map((category, index) => ({
      ...category,
      items: sortItemsAlphabetically(category.items),
      showTaxonomyHeader: index === 0
    }))
  })
}

const handleSearch = ({ results, term, source }) => {
  if (source !== 'suggestion') {
    contentLibraryService.recordLibrarySearch({ term, trigger: 'submit' })
  }

  isSearchActive.value = true
  if (results.length === 0) {
    searchResultCategories.value = []
    return
  }

  searchResultCategories.value = buildGroupedSearchResults(results)
}

const handleSelect = async (item, typedTerm) => {
  contentLibraryService.recordLibrarySearch({
    term: item?.title,
    trigger: 'suggestion',
    itemId: item?.id,
    typedTerm
  })

  if (!item?.id) {
    return
  }

  isSearchActive.value = true

  await nextTick()

  const targetElement = document.getElementById(`content-library-search-item-${item.id}`)
  if (targetElement) {
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

const handleClear = () => {
  isSearchActive.value = false
  searchResultCategories.value = []
  selectedSortBy.value = 'document-name-a-z'
  selectedFilters.value = {
    recipient: null,
    lawScope: null
  }
}

const navigateToTaxonomyGroup = (taxonomyGroupId) => {
  const navigation = customNavigation?.value
  if (!taxonomyGroupId || !navigation || shouldShowLockedState.value) {
    return
  }
  sessionStorageStore.setEvent('contentLibraryTaxonomyInternalQuery', { taxonomyGroupId })
  navigation.navigate({
    newPage: navigation.routes.bcsContentLibraryTaxonomyItems.name,
    query: { taxonomyGroupId }
  })
}

const getItemTitle = (item) => {
  return item.libraryFileName || item.documentDisplayName || 'Untitled item'
}

const getItemDescription = (item) => {
  return item.description || ''
}

const isFavoriteItem = (itemId) => {
  return favoriteItemIds.value.has(itemId)
}

const isFavoriteActionLoading = (itemId) => {
  return favoriteActionLoadingIds.value.has(itemId)
}

const getFavoriteActionLabel = (itemId) => {
  return isFavoriteItem(itemId) ? 'Remove from Favorites' : 'Add to Favorites'
}

const getItemActionLabel = (item) => {
  return isEmbeddableContentLibraryUrl(item?.embedUrl) ? 'Open Tool' : ''
}

const handleItemAction = (item) => {
  if (!isEmbeddableContentLibraryUrl(item?.embedUrl) || !item?.id) {
    return
  }

  const navigation = customNavigation?.value
  if (!navigation) {
    return
  }

  const query = { itemId: item.id }
  sessionStorageStore.setEvent('contentLibraryEmbeddedToolInternalQuery', query)
  navigation.navigate({
    newPage: navigation.routes.contentLibraryEmbeddedTool.name,
    query
  })
}

const copyZoomPassword = async (password) => {
  if (shouldShowLockedState.value) {
    return
  }

  if (!password) {
    return
  }

  try {
    await navigator.clipboard.writeText(password)
    copyPasswordSnackbarMessage.value = 'Password copied'

    if (copyPasswordSnackbarTimeoutId) {
      window.clearTimeout(copyPasswordSnackbarTimeoutId)
    }

    copyPasswordSnackbarTimeoutId = window.setTimeout(() => {
      copyPasswordSnackbarMessage.value = ''
      copyPasswordSnackbarTimeoutId = null
    }, 4500)
  } catch (error) {
    handleErrors('BcsContentLibrary.copyZoomPassword', error)
  }
}

const getDownloadPayloadParts = (payload) => {
  if (payload?.item && payload?.action) {
    return payload
  }

  return {
    item: payload,
    action: null
  }
}

const handleDocumentDownload = async (payload) => {
  if (shouldShowLockedState.value) {
    return
  }

  const { item, action } = getDownloadPayloadParts(payload)
  if (isBrandableContentLibraryItem(item) || action?.isBrandable) {
    handleBrandingDocumentDownload(item, action)
    return
  }

  if (isBrandableContentLibraryItem(item) || !item?.documentFileName) {
    return
  }

  toggleLoadingWithMessage(true, {
    title: 'Downloading document...',
    body: 'Just a few seconds while we prepare the file.'
  })

  try {
    await contentLibraryService.downloadDocument(item)
  } catch (error) {
    handleErrors('BcsContentLibrary.handleDocumentDownload', error)
    reportDownloadFailure(error)
    return
  } finally {
    toggleLoadingWithMessage(false, null)
  }

  try {
    const format = item.documentFileName?.split('.').pop()?.toUpperCase() || ''
    await contentLibraryService.recordItemDownload(item, format)
  } catch (error) {
    handleErrors('BcsContentLibrary.handleDocumentDownload.recordItemDownload', error)
  }
}

const handleBrandingDocumentDownload = (item, action) => {
  if (!isBrandableContentLibraryItem(item) || !action?.format) {
    return
  }

  pendingBrandingDownload.value = { item, action }

  if (logoPath.value) {
    whiteLabelModalRef.value?.openModal()
    return
  }

  downloadPendingBrandingDocument({ printWithCustomLogo: false })
}

const downloadPendingBrandingDocument = async ({ printWithCustomLogo = false } = {}) => {
  const pendingDownload = pendingBrandingDownload.value
  if (!isBrandableContentLibraryItem(pendingDownload?.item) || !pendingDownload?.action?.format) {
    return
  }

  toggleLoadingWithMessage(true, {
    title: 'Preparing your download',
    description: 'Please wait while we get your document ready.'
  })

  try {
    await contentLibraryTemplateService.downloadCachedContentLibraryDocument({
      itemId: pendingDownload.item.id,
      format: pendingDownload.action.format,
      item: pendingDownload.item,
      whiteLabel: printWithCustomLogo
    })
  } catch (error) {
    handleErrors('BcsContentLibrary.downloadPendingBrandingDocument', error)
    reportDownloadFailure(error, { isWhiteLabel: printWithCustomLogo })
    return
  } finally {
    toggleLoadingWithMessage(false, null)
    closeWhiteLabelModal()
  }

  // Record download activity after the overlay is dismissed
  try {
    const format = pendingDownload.action?.format?.toUpperCase() || ''
    await contentLibraryService.recordItemDownload(pendingDownload.item, format)
  } catch (error) {
    handleErrors('BcsContentLibrary.downloadPendingBrandingDocument.recordItemDownload', error)
  }
}

const pendingBrandingDownloadFileType = computed(() => {
  return pendingBrandingDownload.value?.action?.format?.toUpperCase() || 'PDF'
})

const closeWhiteLabelModal = () => {
  whiteLabelModalRef.value?.closeModal()
  pendingBrandingDownload.value = null
}

const toggleFavoriteItem = async (item) => {
  if (shouldShowLockedState.value) {
    return
  }

  if (!item?.id || isFavoriteActionLoading(item.id)) {
    return
  }

  favoriteActionLoadingIds.value.add(item.id)
  try {
    if (isFavoriteItem(item.id)) {
      await contentLibraryService.removeFavoriteItem(item.id)
      favoriteItemIds.value.delete(item.id)
      await contentLibraryService.recordLastLibraryActivity({
        type: 'favorite_removed',
        item
      })
    } else {
      await contentLibraryService.addFavoriteItem(item)
      favoriteItemIds.value.add(item.id)
      await contentLibraryService.recordLastLibraryActivity({
        type: 'favorite_added',
        item
      })
    }

    favoriteItemIds.value = new Set(favoriteItemIds.value)
  } catch (error) {
    handleErrors('BcsContentLibrary.toggleFavoriteItem', error)
  } finally {
    favoriteActionLoadingIds.value.delete(item.id)
    favoriteActionLoadingIds.value = new Set(favoriteActionLoadingIds.value)
  }
}

const hasWhiteLabelingBadge = (category) => {
  return Boolean(logoPath.value && category?.items?.some((item) => isBrandableContentLibraryItem(item)))
}

const getWhiteLabelingTooltipText = (category) => {
  const downloadableItems = (category?.items || []).filter((item) => item.documentFileName || isBrandableContentLibraryItem(item))
  const allDocumentsHaveWhiteLabeling = downloadableItems.length > 0 && downloadableItems.every((item) => isBrandableContentLibraryItem(item))
  return allDocumentsHaveWhiteLabeling
    ? 'All documents in this category have white labeling available.'
    : 'Some documents in this category have white labeling available.'
}

watch(selectedSortBy, () => {
  if (isSearchActive.value) {
    searchResultCategories.value = [...searchResultCategories.value]
  }
})

watch(selectedFilters, () => {
  if (isSearchActive.value) {
    searchResultCategories.value = [...searchResultCategories.value]
  }
}, { deep: true })
</script>

<template>
  <div class="content-library-container bcs-page-padding">
    <div class="mt-4">
      <div class="row">
        <div class="col-7">
          <Breadcrumbs></Breadcrumbs>
          <div v-if="shouldShowLockedState" class="content-library-upgrade-banner">
            <AlertNotification
              :showAlert="true"
              :showCloseButton="false"
              typeAlert="feature"
            >
              <div class="content-library-upgrade-copy d-flex align-items-start gap-3">
                <img :src="padlockAnnouncementIcon" alt="" class="content-library-upgrade-icon" />
                <div class="py-3 px-2">
                <p class="content-library-upgrade-title mb-2">Upgrade your plan to unlock the BCS Content Library plus other premium features.</p>
                <p class="content-library-upgrade-description mb-3">If you choose to upgrade your plan, you’ll have access to a deep well of content that will help you better understand requirements, prepare communications more efficiently, stay ahead of compliance, and more.</p>
                <div class="content-library-upgrade-actions d-flex align-items-center gap-2 flex-wrap">
                  <a
                    v-if="upgradeMailtoHref"
                    :href="upgradeMailtoHref"
                    class="btn content-library-upgrade-primary-button"
                  >
                    Learn More About Plans
                  </a>
                  <button
                    v-if="hasCoreAccess"
                    type="button"
                    class="btn btn-link p-0 text-14 text-primary text-decoration-none content-library-upgrade-secondary-button"
                    @click="goToHelpAndResources"
                  >
                    <strong>Back to Help & Resources Main</strong>
                  </button>
                </div>
                </div>
              </div>
            </AlertNotification>
          </div>

          <div class="content-library-main-content" :class="{ 'content-library-main-content--locked': shouldShowLockedState }">
          <AlertNotification
            v-if="downloadAlert.visible"
            :showAlert="downloadAlert.visible"
            :typeAlert="downloadAlert.type"
            class="content-library-download-alert"
            @update:showAlert="dismissDownloadAlert"
          >
            <span>{{ downloadAlert.message }}</span>
          </AlertNotification>
          <p class="content-library-title text-28">BCS Content Library</p>
          <p class="content-library-subtitle text-14" v-html="contentLibraryIntro"></p>

          <div class="search-container-wrapper">
            <BcsSearchDropdown 
              placeholder="Search the BCS Content Library by keyword or sentence" 
              :items="searchableItems"
              :searchFields="['title', 'description']" 
              :maxSuggestions="10" 
              :resultsCountOverride="isSearchActive ? filteredSearchResultsCount : null"
              @search="handleSearch"
              :arrayTerm="'title'" 
              @select="handleSelect" 
              @clear="handleClear" 
            />
          </div>

          <div class="content-library-divider"></div>

          <div v-if="isInitialLoading" class="content-library-loading-state">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="content-library-loading-copy text-14 text-secondary mb-0">
              Loading content library items...
            </p>
          </div>

          <div v-else-if="!isSearchActive" class="content-library-cards-grid">
            <div
              v-for="item in taxonomyGroupCards"
              :key="item.id"
              class="boxes__container-item d-flex align-items-center flex-column">
              <div class="icon-background d-flex justify-content-center align-items-center">
                <img
                  v-if="taxonomyGroupIcons[item.iconKey]"
                  :src="taxonomyGroupIcons[item.iconKey]"
                  :class="`icon-${item.iconKey}`" />
              </div>
              <h5>{{ item.title }}</h5>
              <p>{{ item.description }}</p>
              <button class="btn btn-primary" @click="navigateToTaxonomyGroup(item.taxonomyGroupId)">
                {{ item.buttonLabel }}
              </button>
            </div>
          </div>

          <div v-else>
            <div class="content-library-items-toolbar">
              <div class="content-library-items-toolbar-group">
                <span class="content-library-sort-label text-14">Filter By</span>
                <div
                  v-for="filterDefinition in countedFilterDefinitions"
                  :key="filterDefinition.key"
                  class="content-library-sort-wrap content-library-sort-wrap--filter"
                >
                  <FilterDropdown
                    :id="filterDefinition.id"
                    v-model="selectedFilters[filterDefinition.key]"
                    :placeholder="filterDefinition.placeholder"
                    :options="filterDefinition.options"
                    :selectedValuePrefix="filterDefinition.selectedValuePrefix"
                    width="w-100"
                  />
                </div>
              </div>
              <div class="content-library-items-toolbar-group content-library-items-toolbar-group--sort">
                <span class="content-library-sort-label text-14">Sort By</span>
                <div class="content-library-sort-wrap">
                  <FilterDropdown
                    id="contentLibrarySearchSortBy"
                    v-model="selectedSortBy"
                    :options="sortByOptions"
                    :hasDefaultSelection="true"
                    width="w-100"
                  />
                </div>
              </div>
            </div>

            <div v-if="!filteredSearchResultCategories.length" class="content-library-items-empty text-14">
              No content items were found for your search.
            </div>

            <div v-else class="content-library-search-results">
              <section
                v-for="category in filteredSearchResultCategories"
                :key="category.id"
                class="content-library-category-section"
              >
                <div v-if="category.showTaxonomyHeader" class="content-library-taxonomy-header">
                  <p class="content-library-taxonomy-title text-24">{{ category.taxonomyGroupTitle }}</p>
                  <p v-if="category.taxonomyGroupDescription" class="content-library-taxonomy-description text-14">
                    {{ category.taxonomyGroupDescription }}
                  </p>
                  <button
                    type="button"
                    class="btn btn-link p-0 text-14 text-primary text-decoration-none content-library-taxonomy-link"
                    @click="navigateToTaxonomyGroup(category.taxonomyGroupId)"
                  >
                    View all content in this section
                  </button>
                </div>

                <div class="content-library-category-header">
                  <div class="content-library-category-title-row">
                    <p class="content-library-category-title text-20">{{ category.name }}</p>
                    <BcsTooltip
                      v-if="hasWhiteLabelingBadge(category)"
                      :simpleText="getWhiteLabelingTooltipText(category)"
                      :showArrow="true"
                      placement="top"
                    >
                      <span class="content-library-white-label-badge text-16 text-primary ms-2 d-flex">
                        <img :src="whiteLabelingAvailableIcon" alt="" class="content-library-white-label-icon" />
                        <strong>
                          White Labeling Available
                        </strong>
                      </span>
                    </BcsTooltip>
                  </div>
                  <p v-if="category.description" class="content-library-category-description text-14">{{ category.description }}</p>
                </div>

                <div class="content-library-category-items">
                  <div class="content-library-items-grid">
                    <ContentLibraryItemCard
                      v-for="item in category.items"
                      :key="item.id"
                      :item="item"
                      :elementId="`content-library-search-item-${item.id}`"
                      :title="getItemTitle(item)"
                      :description="getItemDescription(item)"
                      :documentActionLabel="getDocumentActionLabel(item)"
                      :documentActionIcon="getDocumentActionIcon(item)"
                      :documentActions="getDocumentActions(item)"
                      :actionLabel="getItemActionLabel(item)"
                      :favoriteActionLabel="getFavoriteActionLabel(item.id)"
                      :isFavorite="isFavoriteItem(item.id)"
                      :isFavoriteActionLoading="isFavoriteActionLoading(item.id)"
                      @copy-password="copyZoomPassword"
                      @download-document="handleDocumentDownload"
                      @run-action="handleItemAction"
                      @toggle-favorite="toggleFavoriteItem"
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
          <div v-if="shouldShowLockedState" class="content-library-locked-overlay" aria-hidden="true"></div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <transition name="fade-top-button">
    <button
      v-if="showTopButton"
      type="button"
      class="footer-top-btn"
      title="Top"
      @click="scrollToTop"
    >
      <i class="bi bi-chevron-up"></i>
      <span>Top</span>
    </button>
  </transition>

  <transition name="content-library-copy-snackbar">
    <div
      v-if="copyPasswordSnackbarMessage"
      class="content-library-copy-snackbar"
      role="status"
      aria-live="polite"
    >
      <i class="bi bi-check-circle-fill me-2"></i>
      <span>{{ copyPasswordSnackbarMessage }}</span>
    </div>
  </transition>

  <WhiteLabelModal
    :handleDownload="downloadPendingBrandingDocument"
    :onCloseModal="closeWhiteLabelModal"
    :logoPath="logoPath"
    :logoSize="logoSize"
    :fileType="pendingBrandingDownloadFileType"
    ref="whiteLabelModalRef"
  />

  <Footer></Footer>
</template>

<style scoped lang="scss">
.content-library-container {
  overflow-y: auto;
  min-height: 80vh;
}

.content-library-upgrade-banner {
  margin: 16px 0 20px 8px;
}

.content-library-upgrade-copy {
  width: 100%;
  padding: 0;
}

.content-library-upgrade-icon {
  width: 52px;
  height: 52px;
  object-fit: contain;
  margin-top: 14px;
}

.content-library-upgrade-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}

.content-library-upgrade-description {
  max-width: 900px;
  font-size: 14px;
  line-height: 1.5;
}

.content-library-upgrade-primary-button,
.content-library-upgrade-primary-button:hover,
.content-library-upgrade-primary-button:focus,
.content-library-upgrade-primary-button:active {
  background-color: #6f42c1;
  border-color: #6f42c1;
  color: #ffffff;
  box-shadow: none;
  text-decoration: none;
}

.content-library-main-content {
  position: relative;
}

.content-library-main-content--locked {
  opacity: 0.5;
  filter: grayscale(1) saturate(0.15) brightness(1.02);
}

.content-library-main-content--locked :deep(input),
.content-library-main-content--locked :deep(button),
.content-library-main-content--locked :deep(a),
.content-library-main-content--locked :deep(textarea),
.content-library-main-content--locked :deep(select) {
  pointer-events: none;
}

.content-library-locked-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: transparent;
}

.content-library-title {
  margin-top: 17px;
  margin-left: 9px;
  font-size: 27px;
}

.content-library-subtitle {
  margin-left: 8px;
  margin-top: -10px;
  max-width: 900px;
  width: 100%;
  line-height: 1.4;
  font-weight: normal;
  font-size: 14px;
  margin-bottom: 30px;
}

.search-container-wrapper {
  margin-left: 8px;
  margin-bottom: 26px;
}

.content-library-divider {
  width: auto;
  margin: 0 calc(64% - 64vw) 30px;
  border-top: 1px solid #dee2e6;
}

.content-library-loading-state {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-left: 8px;
}

.content-library-loading-copy {
  text-align: center;
}

.content-library-items-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  margin: 0 0 24px 8px;
  flex-wrap: wrap;
}

.content-library-items-toolbar-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.content-library-items-toolbar-group--sort {
  margin-left: auto;
}

.content-library-sort-label {
  margin: 0;
  color: var(--bs-gray-700);
  font-size: 1rem !important;
  font-weight: 400 !important;
}

.content-library-sort-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 240px;
}

.content-library-sort-wrap--filter {
  min-width: 200px;
}

.content-library-sort-wrap :deep(label) {
  margin-bottom: 4px !important;
  font-size: 1rem !important;
  font-weight: 400 !important;
}

.content-library-sort-wrap :deep(.dropdown) {
  font-family: inherit;
}

.content-library-sort-wrap :deep(.btn-filter-default),
.content-library-sort-wrap :deep(.btn-filter-selected) {
  min-height: 36px;
  padding: 0.25rem 0.625rem !important;
  font-family: inherit !important;
}

.content-library-sort-wrap :deep(.btn.py-2) {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.content-library-sort-wrap--filter :deep(.btn-filter-default.show),
.content-library-sort-wrap--filter :deep(.btn-filter-default:focus),
.content-library-sort-wrap--filter :deep(.btn-filter-default:active) {
  background-color: rgba(111, 66, 193, 0.14) !important;
  border-color: var(--bs-primary) !important;
  color: var(--bs-primary) !important;
}

.content-library-sort-wrap--filter :deep(.btn-filter-default.show .bi-caret-down-fill),
.content-library-sort-wrap--filter :deep(.btn-filter-default:focus .bi-caret-down-fill),
.content-library-sort-wrap--filter :deep(.btn-filter-default:active .bi-caret-down-fill) {
  color: var(--bs-primary) !important;
}

.content-library-sort-wrap :deep(.btn-filter-default .header-20),
.content-library-sort-wrap :deep(.btn-filter-selected .header-20),
.content-library-sort-wrap :deep(.btn-filter-default .header-20 med),
.content-library-sort-wrap :deep(.btn-filter-selected .header-20 med) {
  font-family: inherit !important;
  font-size: 1rem !important;
  font-weight: 400 !important;
}

.content-library-sort-wrap :deep(.header-20),
.content-library-sort-wrap :deep(.option-label.text-16),
.content-library-sort-wrap :deep(.header-20 med),
.content-library-sort-wrap :deep(.dropdown-item .option-label) {
  font-size: 1rem !important;
  line-height: 1.3 !important;
  font-weight: 400 !important;
}

.content-library-sort-wrap :deep(.dropdown-menu) {
  background: #fff;
  border: 1px solid rgba(139, 92, 246, 0.14);
}

.content-library-sort-wrap :deep(.dropdown-item) {
  color: #6f42c1;
}

.content-library-sort-wrap :deep(.dropdown-item .option-label) {
  color: #6f42c1 !important;
  font-weight: 400 !important;
}

.content-library-sort-wrap :deep(.dropdown-item:hover) {
  background-color: #6f42c1;
  color: #ffffff;
}

.content-library-sort-wrap :deep(.dropdown-item:hover .option-label) {
  color: #ffffff !important;
}

.content-library-sort-wrap :deep(.dropdown-item.selected:not(:has(.form-check-input))) {
  background-color: #6f42c1;
}

.content-library-sort-wrap :deep(.dropdown-item.selected:not(:has(.form-check-input)) .option-label) {
  color: #fff !important;
}

.content-library-sort-wrap :deep(.dropdown-item.selected:not(:has(.form-check-input)):hover) {
  background-color: #6f42c1;
}

.content-library-sort-wrap :deep(.dropdown-item.selected:not(:has(.form-check-input)):hover .option-label) {
  color: #fff !important;
}

.content-library-sort-wrap :deep(.bi-caret-down-fill) {
  font-size: 0.75rem;
}

// Make the container wider
.content-library-container .col-7 {
  max-width: 88%;
  flex: 0 0 90%;
}

// Fix breadcrumb styling
:deep(.breadcrumbs) {
  font-weight: normal;
  margin-bottom: 5px;
  margin-left: 5px;
}

.content-library-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 26px 25px;
  margin-top: 38px;
  margin-left: 8px;
}

.boxes__container-item {
  border: 1px solid var(--bs-gray-300);
  width: 100%;
  min-height: 250px;
  border-radius: 4px;
  text-align: center;
  padding: 20px 19px;

  h5,
  p {
    margin: 0;
  }

  h5 {
    font-size: 20px;
    font-weight: 700;
    margin-top: 5px;
  }

  button {
    margin-top: 16px;
    min-height: 34px;
    display: flex;
    font-size: 16px;
    font-weight: normal;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
  }

  p {
    font-size: 14px;
    margin-top: 5px;
  }
}

.content-library-cards-grid .boxes__container-item:last-child:nth-child(odd) {
  grid-column: 1 / 2;
}

.content-library-search-results {
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-left: 8px;
  padding-bottom: 28px;
}

.content-library-taxonomy-header {
  margin-bottom: 14px;
}

.content-library-taxonomy-title {
  margin: 0 0 6px;
  font-weight: 700;
  font-size: 2rem;
  line-height: 1.2;
}

.content-library-taxonomy-description {
  margin: 0 0 8px;
  color: var(--bs-gray-700);
  line-height: 1.4;
}

.content-library-taxonomy-link {
  font-weight: 600;
}

.content-library-category-section {
  padding-top: 10px;
}

.content-library-category-section + .content-library-category-section {
  position: relative;
  margin-top: 24px;
  padding-top: 32px;
}

.content-library-category-section + .content-library-category-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--bs-gray-300);
}

.content-library-category-header {
  margin-bottom: 16px;
}

.content-library-category-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.content-library-category-title {
  margin: 6px 0 6px;
  font-weight: 700;
  font-size: 22px;
}

.content-library-white-label-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.content-library-white-label-icon {
  width: 19px;
  height: 23px;
  flex: 0 0 auto;
}

.content-library-category-description {
  margin: 0 0 18px;
  color: var(--bs-gray-700);
}

.content-library-category-items {
  padding-top: 10px;
  width: 100%;
  max-width: none;
}

.content-library-items-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

.content-library-copy-snackbar {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 40;
  display: inline-flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 10px;
  background: #198754;
  color: #ffffff;
  box-shadow: 0 10px 24px rgba(25, 135, 84, 0.28);
  font-size: 14px;
  font-weight: 600;
}

.content-library-copy-snackbar i {
  font-size: 16px;
}

.content-library-copy-snackbar-enter-active,
.content-library-copy-snackbar-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.content-library-copy-snackbar-enter-from,
.content-library-copy-snackbar-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.content-library-items-empty {
  margin-left: 8px;
  color: var(--bs-gray-700);
  min-height: 180px;
  padding-bottom: 48px;
}

.footer-top-btn {
  position: fixed;
  left: 50%;
  bottom: 72px;
  transform: translateX(-50%);
  z-index: 30;
  width: 58px;
  height: 58px;
  border: none;
  border-radius: 999px;
  background: var(--bs-primary);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  box-shadow: 0 10px 24px rgba(111, 66, 193, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.footer-top-btn:hover,
.footer-top-btn:focus {
  transform: translateX(-50%) translateY(-2px);
  box-shadow: 0 14px 28px rgba(111, 66, 193, 0.35);
}

.footer-top-btn i {
  font-size: 26px;
  line-height: 1;
  transform: translateY(-3px);
  font-weight: 700;
  -webkit-text-stroke: 1.2px currentColor;
  text-shadow:
    0.6px 0 currentColor,
    -0.6px 0 currentColor,
    0 0.6px currentColor,
    0 -0.6px currentColor,
    0.45px 0.45px currentColor,
    -0.45px 0.45px currentColor,
    0.45px -0.45px currentColor,
    -0.45px -0.45px currentColor;
}

.footer-top-btn span {
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  transform: translateY(-6px);
}

.fade-top-button-enter-active,
.fade-top-button-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-top-button-enter-from,
.fade-top-button-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}

.icon-background {
  background-color: var(--bs-gray-200);
  width: 59px;
  height: 59px;
  border-radius: 50%;
}

.icon-learn-understand,
.icon-prepare-communicate,
.icon-plan-track-comply,
.icon-tools-calculators,
.icon-newsletters-updates {
  max-width: 43px;
  max-height: 39px;
}

@media (max-width: 768px) {
  .content-library-items-toolbar {
    justify-content: flex-start;
  }

  .content-library-items-toolbar-group,
  .content-library-items-toolbar-group--sort {
    width: 100%;
    margin-left: 0;
  }

  .content-library-sort-wrap {
    width: calc(100% - 8px);
    min-width: 0;
  }

  .content-library-category-items {
    width: 100%;
  }

  .content-library-category-section + .content-library-category-section::before {
    right: 0;
  }

  .footer-top-btn {
    bottom: 56px;
  }
 }
</style>
